import { useCallback, useMemo, useState } from 'react';
import type { Product } from '../ports';
import { WooCommerceDataProvider } from '../../adapters/catalog/woocommerce';

export interface UseProductsOptions {
  page?: number;
  perPage?: number;
  category?: number;
  brand?: string;
  set?: string;
  search?: string;
  orderBy?: 'date' | 'price' | 'name' | 'popularity';
  order?: 'asc' | 'desc';
  onSale?: boolean;
  featured?: boolean;
}

export function useProducts(initial: UseProductsOptions = {}) {
  const provider = useMemo(() => WooCommerceDataProvider, []);

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchProducts = useCallback(async (opts: UseProductsOptions = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      // Build query object - use exactly what's passed, no stale defaults
      // Only page, perPage, orderBy, order have sensible defaults
      const query: any = {
        page: opts.page ?? 1,
        perPage: opts.perPage ?? 12,
        orderBy: opts.orderBy ?? 'date',
        order: opts.order ?? 'desc',
      };
      
      // Filter values: only include if explicitly set (not undefined)
      // This allows clearing filters by passing undefined
      if (opts.category !== undefined) {
        query.categoryId = opts.category;
      }
      if (opts.search !== undefined && opts.search !== '') {
        query.search = opts.search;
      }
      if (opts.brand !== undefined && opts.brand !== '') {
        query.brand = opts.brand;
      }
      if (opts.set !== undefined && opts.set !== '') {
        query.set = opts.set;
      }
      if (opts.onSale === true) {
        query.onSale = true;
      }
      if (opts.featured === true) {
        query.featured = true;
      }
      
      const resp = await provider.getProducts(query);
      
      // Backend handles all filtering now - no client-side filtering needed
      // This prevents double-filtering issues and ensures consistent results
      const filteredProducts = resp.data;
      
      // Update products atomically to prevent glitching
      setProducts(filteredProducts);
      
      // Prefer server-provided totals when available
      const serverTotal = (resp as any).total;
      const serverTotalPages = (resp as any).totalPages;
      const serverCurrentPage = (resp as any).currentPage;
      
      // Debug logging removed
      
      setTotal(typeof serverTotal === 'number' ? serverTotal : filteredProducts.length);
      setTotalPages(typeof serverTotalPages === 'number' ? serverTotalPages : Math.ceil(filteredProducts.length / (opts.perPage ?? 12)));
      setCurrentPage(typeof serverCurrentPage === 'number' ? serverCurrentPage : (opts.page ?? 1));
    } catch (e: any) {
      setError(e?.message || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, [provider]);

  return {
    products,
    loading,
    error,
    total,
    totalPages,
    currentPage,
    fetchProducts,
  };
}


